/** Browser Web Speech API helpers (no network / API keys required). */

export function isBrowserSpeechSynthesisSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.SpeechSynthesisUtterance !== "undefined"
  );
}

function pickPreferredVoice(
  voices: SpeechSynthesisVoice[],
): SpeechSynthesisVoice | null {
  return (
    voices.find((v) => v.lang === "en-GB") ??
    voices.find((v) => v.lang.startsWith("en-GB")) ??
    voices.find((v) => v.lang.startsWith("en")) ??
    voices[0] ??
    null
  );
}

export async function resolveSpeechVoice(): Promise<SpeechSynthesisVoice | null> {
  if (!isBrowserSpeechSynthesisSupported()) return null;

  const immediate = pickPreferredVoice(window.speechSynthesis.getVoices());
  if (immediate) return immediate;

  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    const finish = () => {
      synth.removeEventListener("voiceschanged", onChange);
      resolve(pickPreferredVoice(synth.getVoices()));
    };
    const onChange = () => finish();
    synth.addEventListener("voiceschanged", onChange);
    window.setTimeout(finish, 400);
  });
}

export function speakWithBrowser(
  text: string,
  voice: SpeechSynthesisVoice | null,
  wasStopped?: () => boolean,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isBrowserSpeechSynthesisSupported()) {
      reject(new Error("Text-to-speech is not supported in this browser."));
      return;
    }

    const synth = window.speechSynthesis;
    synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    if (voice) utterance.voice = voice;
    utterance.lang = voice?.lang ?? "en-GB";
    utterance.rate = 1;
    utterance.onend = () => resolve();
    utterance.onerror = (event) => {
      if (wasStopped?.()) {
        resolve();
        return;
      }
      const code = event.error;
      if (code === "canceled" || code === "interrupted") {
        resolve();
        return;
      }
      reject(new Error("Speech playback failed."));
    };

    // Chrome can drop utterances if speak() runs in the same tick as cancel().
    window.setTimeout(() => synth.speak(utterance), 0);
  });
}

export function stopBrowserSpeech(): void {
  if (!isBrowserSpeechSynthesisSupported()) return;
  window.speechSynthesis.cancel();
}
